import React, { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@finitless/shared/lib";
import { toast } from "sonner";
import { Button, Icon, NativeSelect } from "../../";

const PluginSettingsConfig = ({ config }) => {
  const [value, setValue] = useState(() => {
    if (config.value === null || config.value === undefined) return "";
    if (typeof config.value === "string") return config.value;
    return String(config.value);
  });
  const [isEditing, setIsEditing] = useState(false);
  const queryClient = useQueryClient();

  const updateConfigMutation = useMutation({
    mutationFn: async ({ configId, key, value }) => {
      const response = await apiClient.put(`/configs/${configId}`, {
        key,
        value,
      });
      return response?.result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pluginConfigCategory"] });
      toast.success("Setting updated successfully");
      setIsEditing(false);
    },
    onError: (error) => {
      toast.error(error.message || "Failed to update setting");
    },
  });

  useEffect(() => {
    if (config.value === null || config.value === undefined) {
      setValue("");
    } else if (typeof config.value === "string") {
      setValue(config.value);
    } else {
      setValue(String(config.value));
    }
  }, [config.value]);

  const handleSave = async () => {
    try {
      await updateConfigMutation.mutateAsync({
        configId: config.id,
        key: config.key,
        value: value,
      });
    } catch (error) {
      toast.error(error.message || "Failed to update setting");
    }
  };

  const handleCancel = () => {
    setValue(config.value || "");
    setIsEditing(false);
  };

  const getDisplayText = () => {
    if (config.type === 2 && config.options) {
      const selectedOption = config.options.find(
        (option) => option.value === value,
      );
      return selectedOption ? selectedOption.text : value || "Not set";
    }
    return value || "Not set";
  };

  const renderInput = () => {
    if (!isEditing) {
      return (
        <div className="flex w-full items-center justify-between">
          <span className="text-sm text-gray-300">
            {updateConfigMutation.isPending ? (
              <div className="flex items-center space-x-2">
                <Icon
                  name="Loader"
                  className="h-4 w-4 animate-spin text-blue-400"
                />
                <span>Updating...</span>
              </div>
            ) : (
              getDisplayText()
            )}
          </span>
          <button
            onClick={() => setIsEditing(true)}
            disabled={updateConfigMutation.isPending}
            className="p-1 text-gray-400 transition-colors hover:text-white disabled:cursor-not-allowed disabled:text-gray-600"
            aria-label="Edit setting"
          >
            <Icon name="Edit" className="h-4 w-4" />
          </button>
        </div>
      );
    }

    if (config.type === 2 && config.options) {
      // Select dropdown
      return (
        <div className="flex w-full items-center space-x-2">
          <NativeSelect
            aria-label={config.name}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            disabled={updateConfigMutation.isPending}
            className="custom-select flex-1 rounded-md border border-gray-600 bg-gray-800 px-3 py-2 text-sm text-white focus:ring-1 focus:ring-blue-500 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
          >
            {config.options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.text}
              </option>
            ))}
          </NativeSelect>
          <button
            onClick={handleSave}
            disabled={updateConfigMutation.isPending}
            className="rounded-md bg-green-600 p-2 text-white transition-colors hover:bg-green-700 disabled:cursor-not-allowed disabled:bg-gray-600"
            aria-label="Save"
          >
            {updateConfigMutation.isPending ? (
              <Icon name="Loader" className="h-4 w-4 animate-spin" />
            ) : (
              <Icon name="Check" className="h-4 w-4" />
            )}
          </button>
          <button
            onClick={handleCancel}
            disabled={updateConfigMutation.isPending}
            className="rounded-md bg-gray-600 p-2 text-white transition-colors hover:bg-gray-700 disabled:cursor-not-allowed disabled:bg-gray-500"
            aria-label="Cancel"
          >
            <Icon name="X" className="h-4 w-4" />
          </button>
        </div>
      );
    } else if (config.type === 3 && config.options) {
      // Multi-select (checkboxes)
      const selectedValues = value
        ? value.split("|").filter((v) => v !== "")
        : [];

      const handleMultiSelectChange = (optionValue) => {
        const isSelected = selectedValues.includes(optionValue);
        let newValues;
        if (isSelected) {
          newValues = selectedValues.filter((v) => v !== optionValue);
        } else {
          newValues = [...selectedValues, optionValue];
        }
        setValue(newValues.join("|"));
      };

      return (
        <div className="w-full">
          <div className="mt-2 mb-4 grid grid-cols-2 gap-3 md:grid-cols-3">
            {config.options.map((option) => {
              const isSelected = selectedValues.includes(option.value);
              return (
                <Button
                  type="button"
                  key={option.value}
                  onClick={() => handleMultiSelectChange(option.value)}
                  disabled={updateConfigMutation.isPending}
                  aria-pressed={isSelected}
                  variant="ghost"
                  className={`relative flex cursor-pointer items-center justify-between rounded-lg border-2 p-4 transition-all duration-200 ${
                    isSelected
                      ? "border-blue-500 bg-blue-500/10 shadow-lg shadow-blue-500/20"
                      : "border-gray-600 bg-gray-800/50 hover:border-gray-500 hover:bg-gray-800"
                  } ${
                    updateConfigMutation.isPending
                      ? "cursor-not-allowed opacity-50"
                      : ""
                  } `}
                >
                  <div className="pr-8">
                    <span
                      className={`text-sm font-medium ${
                        isSelected ? "text-blue-300" : "text-gray-300"
                      }`}
                    >
                      {option.text}
                    </span>
                  </div>
                  <div>
                    {isSelected ? (
                      <div className="rounded-full bg-blue-500 p-1">
                        <Icon name="Check" className="h-3 w-3 text-white" />
                      </div>
                    ) : (
                      <div className="h-5 w-5 rounded-full border-2 border-gray-500"></div>
                    )}
                  </div>
                </Button>
              );
            })}
          </div>

          {selectedValues.length > 0 && (
            <div className="mb-3 text-xs font-medium text-blue-400">
              {selectedValues.length}{" "}
              {selectedValues.length === 1 ? "item" : "items"} selected
            </div>
          )}

          <div className="flex items-center justify-end space-x-2">
            <button
              onClick={handleSave}
              disabled={updateConfigMutation.isPending}
              className="rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-green-700 disabled:cursor-not-allowed disabled:bg-gray-600"
            >
              {updateConfigMutation.isPending ? (
                <div className="flex items-center space-x-2">
                  <Icon name="Loader" className="h-4 w-4 animate-spin" />
                  <span>Saving...</span>
                </div>
              ) : (
                <span className="flex items-center space-x-2">
                  <Icon name="Check" className="h-4 w-4" />
                  <span>Save</span>
                </span>
              )}
            </button>
            <button
              onClick={handleCancel}
              disabled={updateConfigMutation.isPending}
              className="rounded-md bg-gray-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-gray-700 disabled:cursor-not-allowed disabled:bg-gray-500"
            >
              Cancel
            </button>
          </div>
        </div>
      );
    } else {
      // Text input (type === 1 or default)
      return (
        <div className="flex w-full items-center space-x-2">
          <input
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            disabled={updateConfigMutation.isPending}
            className="flex-1 rounded-md border border-gray-600 bg-gray-800 px-3 py-2 text-sm text-white focus:ring-1 focus:ring-blue-500 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
            placeholder="Enter value"
          />
          <button
            onClick={handleSave}
            disabled={updateConfigMutation.isPending}
            className="rounded-md bg-green-600 p-2 text-white transition-colors hover:bg-green-700 disabled:cursor-not-allowed disabled:bg-gray-600"
            aria-label="Save"
          >
            {updateConfigMutation.isPending ? (
              <Icon name="Loader" className="h-4 w-4 animate-spin" />
            ) : (
              <Icon name="Check" className="h-4 w-4" />
            )}
          </button>
          <button
            onClick={handleCancel}
            disabled={updateConfigMutation.isPending}
            className="rounded-md bg-gray-600 p-2 text-white transition-colors hover:bg-gray-700 disabled:cursor-not-allowed disabled:bg-gray-500"
            aria-label="Cancel"
          >
            <Icon name="X" className="h-4 w-4" />
          </button>
        </div>
      );
    }
  };

  return (
    <div className="rounded-lg border border-gray-700 bg-gray-800 p-4">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="mb-1 flex items-center space-x-2">
            <h4 className="text-sm font-medium text-white">{config.name}</h4>
          </div>
          {config.description && (
            <p className="mb-1 text-xs text-gray-400">{config.description}</p>
          )}
        </div>
      </div>

      <div className="mt-3 space-y-2">{renderInput()}</div>
    </div>
  );
};

export default PluginSettingsConfig;
